import { inject, Injectable, signal, Inject } from '@angular/core';
import { catchError, finalize, from, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { Auth, authState, OAuthProvider, signInWithEmailAndPassword, signInWithPopup, signOut, User, UserCredential, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from '@angular/fire/auth';
import { AuthFirebaseResponse } from '../interfaces/firebase-resposes.interface';
// import { DEV_FIREBASE_AUTH } from 'src/app/core/tokens/auth.tokens';

/**
 * Servicio de autenticación Firebase.
 *
 * NOTA IMPORTANTE (refactor):
 *  - Antes existían dos apps Firebase simultáneas: `nonprod` (identity, para login
 *    email/password y gestión de cuenta) y `dev` (para generar el token de la API).
 *  - Todo lo relacionado con `identity` / `nonprod` ha sido COMENTADO y la app
 *    única activa es la de `firebaseConfigDev` (csol-conectados-*), que ahora es
 *    la app Firebase por defecto (`inject(Auth)`).
 *  - `nonprodIdToken` y `getNonprodToken()` se mantienen como alias de compatibilidad
 *    para los servicios externos (loans, cts, utilities, etc.) que los consumen;
 *    internamente devuelven el token dev.
 */
@Injectable({providedIn: 'root'})
export class LoginFirebaseService {

  // --- Firebase Identity (nonprod) DESACTIVADO ---
  // private firebaseAuthNonprod = inject(Auth);

  // Firebase Dev (única app activa). Ahora es la app por defecto, así que se inyecta
  // directamente como `Auth` (ya no se usa el token `DEV_FIREBASE_AUTH`).
  private firebaseAuthDev = inject(Auth);

  // Token dev utilizado para TODAS las llamadas a la API (incluido /login/auth).
  readonly firebaseIdToken = signal<string | null>(null);

  // --- Token identity (nonprod) DESACTIVADO ---
  // readonly nonprodIdToken = signal<string | null>(null);

  readonly isAuthReady = signal<boolean>(false);
  readonly lastSignInProvider = signal<string | null>(null);

  constructor() {
    // --- Monitor de sesión de identity (nonprod) DESACTIVADO ---
    // authState(this.firebaseAuthNonprod).subscribe(async (user: User | null) => {
    //     if (user) {
    //       try {
    //         const nonprodTokenResult = await user.getIdTokenResult(true);
    //         this.nonprodIdToken.set(nonprodTokenResult.token);
    //         const signInProvider = nonprodTokenResult.signInProvider;
    //         this.lastSignInProvider.set(signInProvider ?? null);
    //
    //         let devUser = this.firebaseAuthDev.currentUser;
    //
    //         if (!devUser) {
    //             await new Promise(resolve => setTimeout(resolve, 500));
    //             devUser = this.firebaseAuthDev.currentUser;
    //         }
    //
    //         if (devUser) {
    //           const tokenResult = await devUser.getIdTokenResult(true);
    //           this.firebaseIdToken.set(tokenResult.token);
    //         }
    //
    //       } catch (error) {
    //         this.firebaseIdToken.set(null);
    //         this.nonprodIdToken.set(null);
    //       }
    //     } else {
    //       this.firebaseIdToken.set(null);
    //       this.nonprodIdToken.set(null);
    //     }
    //     this.isAuthReady.set(true);
    // });

    // Único monitor activo: sesión de Firebase dev.
    authState(this.firebaseAuthDev).subscribe(async (user: User | null) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult(true);
          this.firebaseIdToken.set(tokenResult.token);
          const signInProvider = tokenResult.signInProvider;
          this.lastSignInProvider.set(signInProvider ?? null);
        } catch (error) {
          this.firebaseIdToken.set(null);
        }
      } else {
        this.firebaseIdToken.set(null);
      }
      this.isAuthReady.set(true);
    });
  }

  private async logoutFromBoth(): Promise<void> {
    // --- signOut de identity DESACTIVADO ---
    // await signOut(this.firebaseAuthNonprod);
    await signOut(this.firebaseAuthDev);
  }

  loginFirebase(email: string, password: string): Observable<AuthFirebaseResponse> {
    // --- Login dual (identity + dev) DESACTIVADO ---
    // return from(signInWithEmailAndPassword(this.firebaseAuthNonprod, email, password)).pipe(
    //   switchMap(async (userCredential: UserCredential) => {
    //     const devUserCredential = await signInWithEmailAndPassword(this.firebaseAuthDev, email, password);
    //     const devTokenResult = await devUserCredential.user.getIdTokenResult();
    //     this.firebaseIdToken.set(devTokenResult.token);
    //     const nonprodTokenResult = await userCredential.user.getIdTokenResult();
    //     this.nonprodIdToken.set(nonprodTokenResult.token);
    //     const signInProvider = nonprodTokenResult.signInProvider;
    //     this.lastSignInProvider.set(signInProvider ?? null);
    //     return { success: true, user: userCredential.user, message: 'Inicio de sesión exitoso.' }
    //   }),
    //   ...
    // );

    // Login único contra Firebase dev.
    return from(signInWithEmailAndPassword(this.firebaseAuthDev, email, password)).pipe(
      switchMap(async (userCredential: UserCredential) => {
        const tokenResult = await userCredential.user.getIdTokenResult();
        this.firebaseIdToken.set(tokenResult.token);

        const signInProvider = tokenResult.signInProvider;
        this.lastSignInProvider.set(signInProvider ?? null);

        return {
          success: true,
          user: userCredential.user,
          message: 'Inicio de sesión exitoso.'
        }
      }),

      // Captura errores del login de Firebase y los propaga como un Observable
      catchError((error) => {
        let errorMessage = 'Ocurrió un error durante el inicio de sesión.';
        switch (error.code) {
          case 'auth/user-not-found':
            errorMessage = 'Usuario no encontrado.';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Contraseña incorrecta.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Email inválido.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Demasiados intentos fallidos. Intenta de nuevo más tarde.';
            break;
          default:
            errorMessage = error.message || errorMessage;
            break;
        }
        return throwError(() => errorMessage)
      }),
    );
  };

  

  loginMicrosoftFirebase(): Observable<AuthFirebaseResponse> {
    const provider = new OAuthProvider('microsoft.com');
    // Microsoft login usa Firebase dev (app única activa).
    return from(signInWithPopup(this.firebaseAuthDev, provider)).pipe(
      switchMap(async (userCredential: UserCredential) => {
        const tokenResult = await userCredential.user.getIdTokenResult();
        const token = tokenResult.token;
        this.firebaseIdToken.set(token);
        const signInProvider = tokenResult.signInProvider;
        this.lastSignInProvider.set(signInProvider ?? null);
        return {
          success: true,
          user: userCredential.user,
          message: 'Inicio de sesión con Microsoft exitoso.'
        };
      }),
      catchError((error) => {

        let errorMessage = 'Ocurrió un error durante el inicio de sesión con Microsoft.';

        // Manejo específico de errores de Firebase Auth
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'El popup de inicio de sesión fue cerrado por el usuario.';
            break;
          case 'auth/cancelled-popup-request':
            errorMessage = 'La solicitud de popup fue cancelada.';
            break;
          case 'auth/unauthorized-domain':
            errorMessage = 'El dominio actual no está autorizado para esta operación.';
            break;
          case 'auth/auth-domain-config-error':
            errorMessage = 'Error de configuración del dominio de autenticación.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Error de red. Verifica tu conexión.';
            break;
          // Puedes añadir más casos de error de Firebase aquí
          default:
            errorMessage = error.message || errorMessage;
            break;
        }
        return throwError(() => errorMessage)
      })
    );
  }

  /**
   * Alias de compatibilidad: originalmente devolvía el token de Firebase identity (nonprod)
   * y es usado por varios servicios (loans, cts, utilities, etc.) para firmar peticiones
   * a microservicios. Tras desactivar identity, devuelve el token de Firebase dev, que es
   * el único disponible. No eliminar sin actualizar a `firebaseIdToken()` en los consumidores.
   */
  async getNonprodToken(): Promise<string | null> {
    try {
      if (!this.firebaseAuthDev) {
        return null;
      }

      const user = this.firebaseAuthDev.currentUser;

      if (user) {
        const token = await user.getIdToken(true);
        this.firebaseIdToken.set(token);
        return token;
      }

      return this.firebaseIdToken();
    } catch (e) {
      return null;
    }
  }

  logoutFirebase(): Observable<boolean> {
    return from(this.logoutFromBoth()).pipe(
      tap(() => {
        this.firebaseIdToken.set(null);
        // this.nonprodIdToken.set(null); // identity desactivado
        this.lastSignInProvider.set(null);
      }),
      map(() => true),
      catchError((error) => {
        return of(false);
      })
    );
  }

  changePasswordFirebase(currentPassword: string, newPassword: string): Observable<boolean> {
    // Cambio de contraseña sobre Firebase dev (antes era sobre identity/nonprod).
    const user = this.firebaseAuthDev.currentUser;
    if( !user ) {
      return throwError(() => new Error('No hay usuario autenticado para cambiar la contraseña.'));
    }

    const credential = EmailAuthProvider.credential(user.email || '', currentPassword);
    return from(reauthenticateWithCredential(user, credential)).pipe(
      // Paso 2: Si la reautenticación es exitosa, procede a actualizar la contraseña
      switchMap(() => from(updatePassword(user, newPassword))),
      map(() => true), // Si llega hasta aquí, la actualización fue exitosa
      catchError((error) => {
        let errorMessage = 'Ocurrió un error al actualizar la contraseña.';
        switch (error.code) {
          case 'auth/wrong-password':
            errorMessage = 'La contraseña actual proporcionada es incorrecta.';
            break;
          case 'auth/weak-password':
            errorMessage = 'La nueva contraseña es demasiado débil (mínimo 6 caracteres).';
            break;
          case 'auth/requires-recent-login':
            errorMessage = 'Esta operación es sensible y requiere reautenticación reciente. Por favor, vuelva a iniciar sesión.';
            break;
          case 'auth/invalid-email':
          case 'auth/user-disabled':
            errorMessage = 'No se pudo actualizar la contraseña. El usuario podría estar deshabilitado o el email inválido.';
            break;
          default:
            errorMessage = error.message || errorMessage;
            break;
        }
        return throwError(() => new Error(errorMessage)); // Propaga el error con un mensaje amigable
      })
    );
  }
}
